// Public exports for shared common package.
import { IsProduction } from './common/EnvProcess';
import { ConsoleLogger } from './internal/ConsoleLogger';

/** Singleton logger provided by the shared package. */
const logger = ConsoleLogger.GetInstance(!IsProduction);

export { logger as Logger };
export { CommonConstants } from './common/CommonConstants';
export { LogStyle } from './enums/LogStyle';
export { Environment } from './enums/Environment';
export { GetSharedSettings, DevKongOverridesMap } from './common/GetSharedSettings';
export {
  ReplaceLocalhostWithIp,
  ShuffleArray,
  ToArray,
  SetDisplayLocale,
  FormatTimestamp,
  ToZonedWallClock,
  ZonedWallClockToUTC,
  FormatDuration,
  GetLocaleRTL,
  FormatLocalized,
  GetLanguageName,
  WrapLTR,
  Capitalize,
  CamelPascalToDisplayText,
  HashString,
  ValidatePassword,
  SanitizeUsername,
  IsValidE164,
  NormalizePhoneE164,
  NormalizeSingleLine,
  NormalizeMultiline,
  IsValidEmail,
  IsPlausibleURL,
  IsValidISO8601,
  IsValidUUID,
  ClampText,
  SanitizeCalendarText,
  RedactPhone,
  RedactEmail,
  SanitizeSignupState,
  SanitizeLogMetadata,
  IsValidLocalizationCode,
  NormalizeLocalizationCode,
  BuildNotificationUsageJob,
  BuildTextMessageUsageJob,
  FormatCountryForPrompt,
  ToJobID,
  SanitizeCSVField,
  ClampLimit,
  ClampOffset,
  HasSmsConsent,
  AppendSenderAttribution,
  CleanExtractedText,
  DeriveTitleFromFilename
} from './common/Helpers';
export {
  GetLocaleList,
  GetLocaleData,
  GetLocaleDataForAgents,
  IsAgentSupportedLocale,
  IsStandardAgentLocale,
  GetAgentLanguageClassification,
  StandardAgentLanguageSubtags,
  LocaleLabels
} from './common/GetLocaleData';
export { GetTimezoneList, GetTimezoneData, GetCountryForTimezone, GetTimezonesForCountry, TimezoneLabels } from './common/GetTimezoneData';
export { GetSupportedCountriesList, GetSupportedCountryData, CountryLabels } from './common/GetSupportedCountriesData';
export { GetCurrencyForCountry, GetSupportedCurrencies, ResolveCurrencyAmount, ResolveSupportedCurrency, FormatCurrencyMinor, GetCurrencySymbol } from './common/Currency';
export { EvaluateAccess } from './common/EvaluateAccess';
export type { IAccessDecision, IAccessSubscription } from './common/EvaluateAccess';
export { IsWithinBusinessHours, ClipWindowToBusinessHours, DescribeBusinessHoursForDay } from './common/BusinessHours';
export { StartRecurringWorker } from './common/RecurringWorker';
export type { IRecurringWorkerOptions } from './common/RecurringWorker';
export { RetryWithBackoff } from './common/Retry';
export type { IRetryWithBackoffOptions } from './common/Retry';
export {
  GetVoiceProviderList,
  GetVoiceProviderData,
  GetDefaultVoiceCatalog,
  ResolveVoiceSelection,
  ResolveVoiceFallbacks,
  ResolveVendorVoice,
  VoiceOptionValues,
  VoiceProviderValues,
  DefaultVoiceOption,
  DefaultVoiceProvider,
  DefaultMultilingualVoiceProvider
} from './common/GetVoiceProviderData';
export { ValidateIPAccessEntry, ConvertIPAccessEntryToFullNetwork, NormalizeIPAccessEntries, IPAccessEntryMatches, NormalizeClientIP } from './common/IPAccess';

// Export all interfaces
export type { ILogger } from './contracts/ILogger';
export type { IRecordLogger } from './contracts/IRecordLogger';
// SaaS provider abstractions (Phase 2)
export type {
  IVoiceProvider,
  IProvisionNumberRequest,
  IProvisionedNumberResult,
  IPhoneNumberCandidate,
  INumberSearchRequest,
  IPrepareProvisionResult,
  IProvisionSelectedRequest,
  IVoiceTranscriptTurn,
  IVoiceCallResult,
  IVoiceTranscriptResult,
  INumberWebhookConfig
} from './contracts/IVoiceProvider';
export type {
  ISchedulingProvider,
  ISchedulingConnectProvider,
  ISchedulingResource,
  IAvailabilityRequest,
  IAvailabilitySlot,
  ICreateBookingRequest,
  IUpdateBookingRequest,
  ISchedulingBookingResult,
  ISchedulingBookingState
} from './contracts/ISchedulingProvider';
export type {
  INotificationProvider,
  ISendSMSRequest,
  ISendEmailRequest,
  INotificationSendResult,
  INotificationDeliveryVerification,
  INotificationDeliveryEvent
} from './contracts/INotificationProvider';
export type { ILLMProvider, ILLMMessage, ILLMCompletionRequest, ILLMCompletionResult, ILLMEmbeddingRequest, ILLMEmbeddingResult, ILLMToolCall, ILLMToolDefinition } from './contracts/ILLMProvider';
export type {
  IBillingProvider,
  ICreateBillingCustomerRequest,
  IBillingCustomerResult,
  ICreateSubscriptionRequest,
  IBillingSubscriptionResult,
  IBillingSubscriptionStateResult,
  IReportUsageRequest,
  IBillingInvoiceResult,
  ICreateCheckoutSessionRequest,
  IBillingCheckoutSessionResult,
  ICreatePortalSessionRequest,
  IBillingPortalSessionResult,
  IEnsureWebhookEndpointRequest,
  IEnsureWebhookEndpointResult,
  IEnsureMetersResult,
  ISyncPlanMeterPrice,
  ISyncPlanCatalogRequest,
  ISyncPlanCatalogResult,
  IArchivePlanCatalogRequest,
  IAddOneTimeInvoiceItemRequest,
  ICreateCouponRequest,
  ICreateCouponResult,
  BillingProrationBehavior,
  IChangeSubscriptionPlanRequest,
  IChangeSubscriptionPlanResult
} from './contracts/IBillingProvider';
export type { IStorageAdapter, IStorageHeadResult, IStoragePutRequest, IStoragePutResult, IStorageSignedURLRequest, IStorageSignedUploadURLRequest } from './contracts/IStorageAdapter';
export type { IWebhookVerifier, IWebhookVerificationRequest } from './contracts/IWebhookVerifier';
export type { IUsageMeter, IUsagePeriodSummaryRequest } from './contracts/IUsageMeter';
export type { IUsageMeterJob } from './contracts/IUsageMeterJob';
export type { IPostCallJob } from './contracts/IPostCallJob';
export type { ITextMessageJob } from './contracts/ITextMessageJob';
export type { IKnowledgeIngestJob } from './contracts/IKnowledgeIngestJob';
export type { KnowledgeIngestScope } from './contracts/IKnowledgeIngestJob';
// SaaS domain DTOs (Phase 2)
export type { ITenantDTO } from './models/ITenantDTO';
export type { IOnboardingStatusDTO } from './models/IOnboardingStatusDTO';
export type { IProvisionPrepareDTO } from './models/IProvisionPrepareDTO';
export type { ITenantUserDTO } from './models/ITenantUserDTO';
export type { IBusinessProfileDTO, IBusinessHoursWindow, IBusinessService } from './models/IBusinessProfileDTO';
export type { IPhoneNumberDTO } from './models/IPhoneNumberDTO';
export type { IAdminTenantNumberDTO } from './models/IAdminTenantNumberDTO';
export type { IWebhookVerifyIssueDTO, IWebhookVerifyResultDTO } from './models/IWebhookVerifyResultDTO';
export type { ICallDTO } from './models/ICallDTO';
export type { ICallTranscriptDTO, ICallTranscriptTurn } from './models/ICallTranscriptDTO';
export type { ICallOutcomeDTO } from './models/ICallOutcomeDTO';
export type { ITextDTO } from './models/ITextDTO';
export type { ITextMessageDTO } from './models/ITextMessageDTO';
export type { ILeadDTO } from './models/ILeadDTO';
export type { IBookingDTO } from './models/IBookingDTO';
export type { IIntegrationDTO, IntegrationKind } from './models/IIntegrationDTO';
export type { IKnowledgeDocumentDTO } from './models/IKnowledgeDocumentDTO';
export type { IKnowledgeChunkDTO } from './models/IKnowledgeChunkDTO';
export type { ICoreKnowledgeDocumentDTO, KnowledgeSourceType } from './models/ICoreKnowledgeDocumentDTO';
export type { ICoreKnowledgeChunkDTO } from './models/ICoreKnowledgeChunkDTO';
export type { INotificationDTO } from './models/INotificationDTO';
export type { IWebhookEventDTO } from './models/IWebhookEventDTO';
export type { IAuditLogDTO, AuditActorType } from './models/IAuditLogDTO';
export type { IBillingAuditLogDTO, BillingAuditActorType } from './models/IBillingAuditLogDTO';
export type {
  IArchiveDTO,
  IArchiveTriggerRequestDTO,
  IArchiveRunResultDTO,
  IArchiveRunAllResultDTO,
  IArchiveSourceRunMetadataDTO,
  IArchiveMetadataDTO,
  IArchiveListResponseDTO,
  ArchiveSourceType
} from './models/IArchiveDTO';
// SaaS billing DTOs (Phase 2)
export type { IBillingCustomerDTO } from './models/IBillingCustomerDTO';
export type { ISubscriptionDTO } from './models/ISubscriptionDTO';
export type { IPlanDTO, IPlanMeterPrice } from './models/IPlanDTO';
export type { INicheContentDTO } from './models/INicheContentDTO';
export type { IUsageEventDTO, UsageSourceType } from './models/IUsageEventDTO';
export type { IUsagePeriodSummaryDTO, IUsageMeterTotal } from './models/IUsagePeriodSummaryDTO';
export type { IUsagePeriodSummaryRecordDTO } from './models/IUsagePeriodSummaryRecordDTO';
export type { IInvoiceDTO } from './models/IInvoiceDTO';
export type { IPaymentEventDTO, PaymentEventType } from './models/IPaymentEventDTO';
export type { IProviderCostEventDTO, ProviderCostSourceType } from './models/IProviderCostEventDTO';
export type { IRecordLogDTO, RecordLogCategory, RecordLogSource } from './models/IRecordLogDTO';
export type { ISystemAlertDTO, SystemAlertSeverity, SystemAlertStatus } from './models/ISystemAlertDTO';
export type { ILocalizationDTO, ILocalizationDetailDTO, ILocalizationLocaleDTO, LocalizationKind, LocalizationStatus } from './models/ILocalizationDTO';
export type { IUserDTO } from './models/IUserDTO';
export type { ISessionDTO } from './models/ISessionDTO';
export type { IUserPermissionsDTO } from './models/IUserPermissionsDTO';
export type { IGlobalSettingsDTO, ICallRateLimitsDTO, ICallRateLimitTierDTO } from './models/IGlobalSettingsDTO';
export type { ICouponDTO } from './models/ICouponDTO';
export type { IPortalSettingsDTO } from './models/IPortalSettingsDTO';
export type { ILobbySettingsDTO } from './models/ILobbySettingsDTO';
export type { BackupHealthState, IBackupHealthDTO, IBackupSnapshotDTO, IFlushBackupResponseDTO } from './models/IBackupDTO';

// Export all types
export type { SharedSettingsMap } from './common/GetSharedSettings';
export type { ILocaleData } from './common/GetLocaleData';
export type { ICountryData } from './common/GetSupportedCountriesData';
export type { VoiceOption, VoiceProvider, IVoiceSelection, IVoiceProviderData, IVendorVoiceSelection } from './common/GetVoiceProviderData';
// Database types
export type { Scope } from './types/Scope';
export type { Status } from './types/Status';
export type { OnboardingStep } from './types/OnboardingStep';
export type { UserRole } from './types/UserRole';
export type { LocalizationCode } from './types/LocalizationCode';
// SaaS domain types (Phase 2)
export type { CallStatus } from './types/CallStatus';
export type { CallIntent } from './types/CallIntent';
export type { CallSourceChannel } from './types/CallSourceChannel';
export type { CallType } from './types/CallType';
export type { NotificationChannel } from './types/NotificationChannel';
export type { TextChannel } from './types/TextChannel';
export type { TextDirection } from './types/TextDirection';
export type { TextSenderKind } from './types/TextSenderKind';
export type { ActionStatus } from './types/ActionStatus';
export type { BillingStatus } from './types/BillingStatus';
export type { BillingCurrency } from './types/BillingCurrency';
export type { MeterKey } from './types/MeterKey';
// Database type values
export { ScopeValues } from './types/Scope';
export { StatusValues } from './types/Status';
export { OnboardingStepValues } from './types/OnboardingStep';
export { UserRoleValues } from './types/UserRole';
export { LocalizationCodeValues } from './types/LocalizationCode';
// SaaS domain type values (Phase 2)
export { CallStatusValues } from './types/CallStatus';
export { CallIntentValues } from './types/CallIntent';
export { CallSourceChannelValues } from './types/CallSourceChannel';
export { CallTypeValues } from './types/CallType';
export { NotificationChannelValues } from './types/NotificationChannel';
export { TextChannelValues } from './types/TextChannel';
export { TextDirectionValues } from './types/TextDirection';
export { TextSenderKindValues } from './types/TextSenderKind';
export { ActionStatusValues } from './types/ActionStatus';
export { BillingStatusValues } from './types/BillingStatus';
export { BillingCurrencyValues } from './types/BillingCurrency';
export { MeterKeyValues } from './types/MeterKey';
